<template>
  <div class="home">
    <!-- Skip to main content link for accessibility -->
    <a href="#main" class="skip-link">Skip to main content</a>

    <!-- Background orbs -->
    <div class="orb-container" aria-hidden="true">
      <div class="orb orb-1"></div>
      <div class="orb orb-2"></div>
      <div class="orb orb-3"></div>
    </div>

    <!-- Globe component -->
    <Globe />

    <!-- Navigation -->
    <Navigation />

    <!-- Globe window section (standard mode) -->
    <section id="globe-standard" style="pointer-events: none">
      <!-- Globe window - standard mode (no flows) -->
    </section>

    <!-- Hero Section -->
    <HeroSection />

    <!-- What we do -->
    <CardSection
      id="what"
      title="What we do"
      lead="Placement done the right way: targeted, rigorous, and respectful of LPs' time."
      :cards="whatWeDoCards"
      style="pointer-events: none"
    />

    <!-- How we work -->
    <RailSection
      id="how"
      title="How we work"
      lead="Selective by design. Embedded with our managers. Transparent with our LPs."
      :steps="howWeWorkSteps"
      style="pointer-events: none"
    />

    <!-- Focus & edge -->
    <CardSection
      id="focus"
      title="Focus & edge"
      lead="We specialize in strategies where depth of underwriting, origination advantage, and structural protection matter most."
      :cards="focusCards"
      style="pointer-events: none"
    />

    <!-- Globe window section -->
    <section id="globe-window" style="pointer-events: none">
      <!-- Globe window - reveals the persistent background globe -->
    </section>

    <!-- Contact Section -->
    <ContactSection />

    <!-- Command Palette -->
    <CommandPalette ref="commandPaletteRef" />
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { useAppStore } from '@/stores/app';
import { useDevice } from '@/composables/useDevice';
import { useAnalytics } from '@/composables/useAnalytics';
import Globe from '@/components/Globe.vue';
import Navigation from '@/components/Navigation.vue';
import CommandPalette from '@/components/CommandPalette.vue';
import HeroSection from '@/components/HeroSection.vue';
import CardSection from '@/components/CardSection.vue';
import RailSection from '@/components/RailSection.vue';
import ContactSection from '@/components/ContactSection.vue';

const store = useAppStore();
const commandPaletteRef = ref(null);

// Initialize device detection and analytics
const device = useDevice();
const analytics = useAnalytics({
  autoTrack: true,
  debug: import.meta.env.DEV
});

// Content data
const whatWeDoCards = ref([
  {
    kicker: 'Placement',
    title: 'Institutional capital raising',
    description: 'Lead and co-lead mandates for funds and bespoke vehicles. Tight targeting, disciplined sequencing, and clear LP materials that sharpen the narrative—not drown it.'
  },
  {
    kicker: 'Advisory',
    title: 'Readiness & positioning',
    description: 'Strategy articulation, data-room and DDQ readiness, fee & terms calibration, pipeline planning, and manager references—so you enter market confident and credible.'
  },
  {
    kicker: 'LP Coverage',
    title: 'Real relationships',
    description: 'Diversified global LP access built on trust and follow-through. We aim for fit over volume, cultivating durable partnerships on both sides of the table.'
  }
]);

const howWeWorkSteps = ref([
  {
    title: 'Selective',
    description: 'We back a small number of managers at a time to preserve quality of execution and avoid channel conflict.'
  },
  {
    title: 'Methodical',
    description: 'Clear segmentation, staged outreach, and tight feedback loops—turning interest into diligence into commitments.'
  },
  {
    title: 'Aligned',
    description: 'Simple, aligned economics and senior attention throughout. No hand-offs, no black boxes.'
  },
  {
    title: 'LP-first',
    description: 'Concise materials, honest positioning, and a respect-for-time culture that LPs appreciate.'
  }
]);

const focusCards = ref([
  {
    kicker: 'Private Credit',
    title: 'Performing & specialty',
    description: 'Senior, unitranche, asset-backed, NAV, opportunistic, and niche specialty finance platforms with durable edge.'
  },
  {
    kicker: 'Insurance / ILS',
    title: 'Risk transfer',
    description: 'Cat and specialty re, collateralized structures, and partnerships aligned with insurance balance-sheet needs.'
  },
  {
    kicker: 'Special Situations',
    title: 'Uncorrelated & complex',
    description: 'Litigation finance and other idiosyncratic, process-driven strategies where outcomes are about skill, not beta.'
  }
]);

// Lifecycle
onMounted(() => {
  console.log('✓ Vue application mounted');

  // Initialize fade-in animations
  const fadeElements = document.querySelectorAll('.fade-in');
  const observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        entry.target.style.opacity = '1';
        entry.target.style.transform = 'translateY(0)';
      }
    });
  }, {
    threshold: 0.1,
    rootMargin: '0px 0px -50px 0px'
  });

  fadeElements.forEach(el => {
    el.style.opacity = '0';
    el.style.transform = 'translateY(20px)';
    el.style.transition = 'opacity 0.6s ease, transform 0.6s ease';
    observer.observe(el);
  });

  // Watch for Contact section (Telephone Booth Morph)
  const contactSection = document.getElementById('contact');
  if (contactSection) {
    const contactObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting && entry.intersectionRatio > 0.1) {
            store.setLetsTalkActive(true);
        } else if (!entry.isIntersecting) {
            store.setLetsTalkActive(false);
        }
      });
    }, { threshold: 0.1 });
    contactObserver.observe(contactSection);
  }
});

// Expose command palette to window for external access if needed
if (typeof window !== 'undefined') {
  window.openCommandPalette = () => {
    commandPaletteRef.value?.open();
  };
}
</script>

<style>
/* Global styles are imported from styles.css */
/* App-specific styles can go here */

.skip-link {
  position: absolute;
  top: -40px;
  left: 0;
  background: var(--fg);
  color: var(--bg);
  padding: 8px;
  text-decoration: none;
  z-index: 100;
}

.skip-link:focus {
  top: 0;
}
</style>
